import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Package, Printer, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface DashboardData {
  ordersThisMonth: number;
  revenueThisMonth: number;
  expensesThisMonth: number;
  pendingOrders: number;
  printersActive: number;
  filamentLowStock: Array<{
    id: number;
    brand: string;
    material: string;
    color: string;
    colorHex?: string;
    weightRemainingG: number;
    lowStockAlertG: number;
  }>;
}

interface RecentOrder {
  id: number;
  guestName?: string;
  customer?: { name: string };
  total: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
}

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      const [dashRes, ordersRes] = await Promise.all([
        api.get<DashboardData>("/dashboard").catch(() => null),
        api.get<{ items: RecentOrder[] }>("/orders?pageSize=5").catch(() => null),
      ]);

      if (dashRes?.data) {
        setData(dashRes.data);
      }
      if (ordersRes?.data?.items) {
        setRecentOrders(ordersRes.data.items);
      }
    } catch {
      // Fallback a estados silenciosos
    } finally {
      setIsLoading(false);
    }
  };

  const stats = [
    {
      title: "Ingresos del mes",
      value: data ? `$${data.revenueThisMonth.toLocaleString("es-AR")}` : "$0",
      icon: DollarSign,
      color: "text-emerald-600",
    },
    {
      title: "Gastos del mes",
      value: data ? `$${data.expensesThisMonth.toLocaleString("es-AR")}` : "$0",
      icon: DollarSign,
      color: "text-amber-600",
    },
    {
      title: "Pedidos activos",
      value: data ? data.pendingOrders.toString() : "0",
      icon: Package,
      color: "text-primary",
    },
    {
      title: "Impresoras activas",
      value: data ? data.printersActive.toString() : "0",
      icon: Printer,
      color: "text-blue-600",
    },
  ];

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Métricas clave del taller y pedidos en producción
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/workshop">Ver Taller Kanban</Link>
          </Button>
          <Button asChild size="sm">
            <Link to="/orders">Gestionar Pedidos</Link>
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        {stats.map((stat, i) => (
          <Card key={i} className="border-border/80 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.color || "text-muted-foreground"}`} />
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold">
                {isLoading ? "..." : stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
        {/* Últimos Pedidos */}
        <Card className="col-span-4 border-border/80 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Últimos Pedidos</CardTitle>
            <Button asChild variant="ghost" size="sm" className="text-xs gap-1">
              <Link to="/orders">
                Ver todos <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No hay pedidos recientes registrados.
              </div>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => {
                  const clientName =
                    order.customer?.name || order.guestName || `Cliente #${order.id}`;
                  return (
                    <div
                      key={order.id}
                      className="flex items-center justify-between border-b border-border/50 pb-3 last:border-0 last:pb-0"
                    >
                      <div>
                        <p className="font-medium text-sm">{clientName}</p>
                        <p className="text-xs text-muted-foreground">
                          Pedido #{order.id} • {order.paymentStatus === "paid" ? "Pagado" : "Pendiente"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm">
                          ${order.total.toLocaleString("es-AR")}
                        </p>
                        <Badge variant="outline" className="text-[10px] uppercase font-mono">
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alertas de Filamento / Stock Bajo */}
        <Card className="col-span-3 border-border/80 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Stock Bajo de Filamento
            </CardTitle>
            <Button asChild variant="ghost" size="sm" className="text-xs">
              <Link to="/inventory">Inventario</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {(!data?.filamentLowStock || data.filamentLowStock.length === 0) ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                Todos los filamentos tienen stock suficiente. ✨
              </div>
            ) : (
              <div className="space-y-3">
                {data.filamentLowStock.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between border-b border-border/40 pb-2.5 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className="h-3.5 w-3.5 rounded-full border shadow-xs shrink-0"
                        style={{ backgroundColor: item.colorHex || "#333" }}
                      />
                      <div>
                        <p className="font-medium text-xs md:text-sm">
                          {item.brand} ({item.material})
                        </p>
                        <p className="text-[11px] text-muted-foreground">{item.color}</p>
                      </div>
                    </div>
                    <Badge variant="destructive" className="text-[11px] font-mono">
                      {item.weightRemainingG}g restantes
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
